import { ActionIcon, Button, Card, Checkbox, Popover, Tabs, TextInput, Tooltip } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import type { IssueStatus, IssueType } from '@prisma/client';
import { IconCheck, IconExclamationCircle, IconSearch, IconX } from '@tabler/icons-react';
import { createColumnHelper, getCoreRowModel, getFilteredRowModel, useReactTable } from '@tanstack/react-table';
import clsx from 'clsx';
import Link from 'next/link';
import { useMemo, useState } from 'react';
import { capFirst } from '~/common/string/capitalize';
import TimeAgo from '~/components/time/TimeAgo';
import { useProjectId } from '~/hooks/context/useProjectId';
import { useAuthData } from '~/hooks/auth/use-auth-data';
import type { IssueListItem } from '~/server/api/routers/issues';
import { api } from '~/trpc-extra/api';
import IssueTypeBadge from './IssueTypeBadge';
import QuickViewIssueTestRuns from './QuickViewIssueTestRuns';

const columnHelper = createColumnHelper<IssueListItem>();

const COLUMNS = [
  columnHelper.accessor('id', {
    header: 'Id',
  }),
  columnHelper.accessor('title', {
    header: 'Title',
  }),
  columnHelper.accessor('description', {
    header: 'Description',
  }),
  columnHelper.accessor('issueType', {
    header: 'IssueType',
  }),
  columnHelper.accessor('firstSeen', {
    header: 'First Seen',
  }),
  columnHelper.accessor('lastSeen', {
    header: 'Last Seen',
  }),
  columnHelper.accessor('numAffectedTestRuns', {
    header: '# Affected Tests',
  }),
];

export type PrimaryAction = 'resolve' | 'merge' | 'connect' | 'delete';

// TODO: Titles
// // TODO: Save better error info to give better name
// ---
// TODO: Pagination
// TODO: Sorting
export default function IssueTable({
  className,
  primaryAction,
  primaryActionFunc,
  hideIssueId,
  openLinksInNewTab,
  singleSelectionMode,
}: {
  className?: string;
  primaryAction?: PrimaryAction;
  primaryActionFunc?: (issueIds: string[]) => Promise<void>;
  hideIssueId?: string;
  openLinksInNewTab?: boolean;
  singleSelectionMode?: boolean;
}) {
  const trpcUtils = api.useUtils();
  const projectId = useProjectId();
  const { isAdmin } = useAuthData();
  const [issueStatusFilter, setIssueStatusFilter] = useState<IssueStatus>('UNRESOLVED');
  const updateAction = issueStatusFilter === 'RESOLVED' ? 'UNRESOLVED' : 'RESOLVED';
  const [isProcessing, setIsProcessing] = useState(false);
  const { data: issues } = api.issues.list.useQuery(
    { projectId: projectId!, statusFilter: issueStatusFilter },
    { enabled: !!projectId },
  );
  const updateIssues = api.issues.update.useMutation();
  const deleteIssues = api.issues.deleteMany.useMutation();

  const linkOpenBehavior = openLinksInNewTab ? { rel: 'noopener noreferrer', target: '_blank' } : {};

  // Prevents infinite re-renders when changing `statusFilter` (https://github.com/TanStack/table/issues/4614)
  const memoizedIssues = useMemo(
    () => (hideIssueId ? issues?.filter((i) => i.id !== hideIssueId) || [] : issues || []),
    [issues, hideIssueId],
  );

  const columns = useMemo(() => COLUMNS, []);
  const table = useReactTable({
    columns,
    data: memoizedIssues,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getRowId: (row) => row.id,
    globalFilterFn: 'includesString',
  });
  const rowModel = table.getRowModel();
  const tableState = table.getState();

  // Determine the state of the header checkbox
  const numSelectedRows = Object.values(tableState.rowSelection).filter(Boolean).length;
  const totalRows = rowModel.rows.length;
  const isAllRowsSelected = totalRows > 0 && numSelectedRows === totalRows;
  const isSomeRowsSelected = numSelectedRows > 0 && numSelectedRows < totalRows;

  const headerCheckboxState = useMemo(
    () =>
      isAllRowsSelected ? 'checked'
      : isSomeRowsSelected ? 'intermediate'
      : 'unchecked',
    [isAllRowsSelected, isSomeRowsSelected],
  );

  return (
    <div className={className}>
      <div>
        <Tabs
          defaultValue="UNRESOLVED"
          className="mb-2"
          onChange={(statusFilter) => setIssueStatusFilter(statusFilter as IssueStatus)}
        >
          <Tabs.List>
            <Tabs.Tab value="UNRESOLVED">Unresolved</Tabs.Tab>
            <Tabs.Tab value="RESOLVED">Resolved</Tabs.Tab>
          </Tabs.List>
        </Tabs>
      </div>
      <div className="mb-5">
        <TextInput
          placeholder="Search Issues"
          onChange={(e) => table.setGlobalFilter(e.target.value)}
          value={tableState.globalFilter || ''}
          leftSection={<IconSearch size={14} />}
          rightSection={
            tableState.globalFilter ?
              <ActionIcon
                variant="transparent"
                title="Clear Search"
              >
                <IconX
                  size={18}
                  onClick={() => table.setGlobalFilter('')}
                />
              </ActionIcon>
            : null
          }
        />
      </div>
      {/* Header */}
      <Card
        withBorder
        className="rounded-b-none rounded-t-lg dark:bg-[#292929]"
      >
        <div className="grid w-full grid-cols-[5fr_1fr_1fr_1fr] gap-x-8 xl:grid-cols-[7fr_1fr_1fr_1fr]">
          <div className="flex flex-row items-center gap-2 text-stone-600 dark:text-stone-400">
            {!singleSelectionMode && (
              <Checkbox
                indeterminate={headerCheckboxState === 'intermediate'}
                checked={headerCheckboxState === 'checked'}
                onClick={table.getToggleAllPageRowsSelectedHandler()}
                // Adding readOnly to make React happy
                readOnly
              />
            )}

            {(primaryAction === 'merge' || primaryAction === 'connect') && (
              <Button
                disabled={!(isSomeRowsSelected || isAllRowsSelected)}
                onClick={async () => {
                  const selectedIds = table.getSelectedRowModel().rows.map(({ id }) => id);
                  setIsProcessing(true);
                  try {
                    await primaryActionFunc?.(selectedIds);
                  } catch (e) {
                    console.error(e);
                    notifications.show({
                      message: `Error performing actions`,
                      color: 'red',
                      icon: <IconExclamationCircle size="1rem" />,
                    });
                  } finally {
                    setIsProcessing(false);
                  }
                }}
              >
                {capFirst(primaryAction.toLowerCase())}
              </Button>
            )}
            {(!primaryAction || primaryAction === 'resolve') && (
              <Button
                size="compact-sm"
                variant="light"
                color="green"
                disabled={!(isSomeRowsSelected || isAllRowsSelected) || isProcessing}
                className="first-letter:capitalize"
                onClick={async () => {
                  const action = updateAction;
                  const selectedIds = table.getSelectedRowModel().rows.map(({ id }) => id);
                  console.log(table.getSelectedRowModel().rows);
                  console.log('selectedIds', selectedIds);
                  setIsProcessing(true);
                  try {
                    await updateIssues.mutateAsync({
                      projectId: projectId!,
                      issueIds: selectedIds,
                      update: { status: action },
                    });
                    await Promise.allSettled([
                      trpcUtils.issues.list.invalidate().catch(() => undefined),
                      trpcUtils.issues.get.invalidate().catch(() => undefined),
                      trpcUtils.issues.getActivity.invalidate().catch(() => undefined),
                    ]);
                    notifications.show({
                      message: `${capFirst(action.toLowerCase())} ${selectedIds.length} issue${
                        selectedIds.length === 1 ? '' : 's'
                      }`,
                      color: updateAction === 'UNRESOLVED' ? 'red' : 'green',
                      icon: <IconCheck size="1rem" />,
                    });
                    table.resetRowSelection(true);
                  } catch (e) {
                    console.error(e);
                    notifications.show({
                      message: `Error updating issues`,
                      color: 'red',
                      icon: <IconExclamationCircle size="1rem" />,
                    });
                  } finally {
                    setIsProcessing(false);
                  }
                }}
              >
                {capFirst(updateAction.toLowerCase().slice(0, -1))}
              </Button>
            )}
            {(!primaryAction || primaryAction === 'resolve') && !!isAdmin && (
              <Button
                size="compact-sm"
                variant="light"
                color="red"
                disabled={!(isSomeRowsSelected || isAllRowsSelected) || isProcessing}
                onClick={async () => {
                  const selectedIds = table.getSelectedRowModel().rows.map(({ id }) => id);
                  setIsProcessing(true);
                  try {
                    await deleteIssues.mutateAsync({
                      projectId: projectId!,
                      issueIds: selectedIds,
                    });
                    await Promise.allSettled([
                      trpcUtils.issues.list.invalidate().catch(() => undefined),
                      trpcUtils.issues.get.invalidate().catch(() => undefined),
                      trpcUtils.issues.getActivity.invalidate().catch(() => undefined),
                    ]);
                    notifications.show({
                      message: `Deleted ${selectedIds.length} issue${selectedIds.length === 1 ? '' : 's'}`,
                      color: 'green',
                      icon: <IconCheck size="1rem" />,
                    });
                    table.resetRowSelection(true);
                  } catch (e) {
                    console.error(e);
                    notifications.show({
                      message: `Error deleting issues`,
                      color: 'red',
                      icon: <IconExclamationCircle size="1rem" />,
                    });
                  } finally {
                    setIsProcessing(false);
                  }
                }}
              >
                Delete
              </Button>
            )}
          </div>
          {/* <div className="text-sm font-bold text-stone-600 dark:text-stone-400"> */}
          <Tooltip
            label="List of test runs affected by this issue"
            color="rgba(156, 156, 156, 1)"
          >
            <div className="flex justify-end px-1 text-sm font-bold text-stone-600 dark:text-stone-400">
              # Test Runs
            </div>
          </Tooltip>
          <div className="flex justify-end px-1 text-sm font-bold text-stone-600 dark:text-stone-400">Last Seen</div>
          <div className="flex justify-end px-1 text-sm font-bold text-stone-600 dark:text-stone-400">First Seen</div>

          {/* </div> */}
        </div>
      </Card>
      {/* Body */}
      <div>
        {rowModel.rows.map((rowEl, idx) => {
          const cells = rowEl.getVisibleCells();

          // TODO: I hope there's a less ugly way to get these values
          const issueId = cells.find((cell) => cell.column.id === 'id')?.getValue() as string;
          const title = cells.find((cell) => cell.column.id === 'title')?.getValue() as string;
          const description = cells.find((cell) => cell.column.id === 'description')?.getValue() as string;
          const issueType = cells.find((cell) => cell.column.id === 'issueType')?.getValue() as IssueType;
          const numAffectedTestRuns = cells
            .find((cell) => cell.column.id === 'numAffectedTestRuns')
            ?.getValue() as number;
          const lastSeen = cells.find((cell) => cell.column.id === 'lastSeen')?.getValue() as Date;
          const firstSeen = cells.find((cell) => cell.column.id === 'firstSeen')?.getValue() as Date;

          const onClick = () => {
            if (singleSelectionMode) {
              table.resetRowSelection(true);
            }
            rowEl.toggleSelected();
          };

          return (
            <Card
              key={rowEl.id}
              withBorder
              className={clsx({
                'rounded-t-none border-t-0': idx === 0,
                'border-b-0': idx !== rowModel.rows.length - 1,
              })}
            >
              <div
                className="grid w-full grid-cols-[5fr_1fr_1fr_1fr] gap-x-8 xl:grid-cols-[7fr_1fr_1fr_1fr]"
                onClick={onClick}
              >
                <div className="flex flex-row items-center">
                  <Checkbox
                    checked={rowEl.getIsSelected()}
                    onChange={onClick}
                    className="mr-5"
                  />
                  <div className="flex flex-col">
                    <Link
                      className={`text-sm font-bold tracking-normal ${
                        issueStatusFilter === 'UNRESOLVED' ? 'text-blue-500' : 'text-gray-600 dark:text-gray-400'
                      }`}
                      href={`/project/${projectId}/issues/${issueId}`}
                      {...linkOpenBehavior}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {title}
                    </Link>
                    <span className="text-sm tracking-normal text-gray-800 opacity-80 dark:text-gray-200">
                      {description || ''}
                    </span>
                    <div className="flex items-center space-x-4 pt-2">
                      {issueType !== 'OTHER' && (
                        <IssueTypeBadge
                          issueType={issueType}
                          isResolved={issueStatusFilter === 'RESOLVED'}
                        />
                      )}
                      <div className="text-xs">
                        {/* <div className="flex cursor-pointer items-center space-x-[0.1em] underline ">
                          <IconBulb size={15} />
                          <span>3 fixes</span>
                        </div> */}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-end px-4">
                  <Popover
                    shadow="md"
                    withArrow
                  >
                    <Popover.Target>
                      {/* Increase px for a larger click area */}
                      <span className="px-5 text-sm font-medium text-gray-700 hover:cursor-pointer hover:underline dark:text-gray-300">
                        {numAffectedTestRuns}
                      </span>
                    </Popover.Target>
                    <Popover.Dropdown>
                      <div className="flex w-[250px] flex-col space-y-2 p-2">
                        <QuickViewIssueTestRuns issueId={issueId} />
                      </div>
                    </Popover.Dropdown>
                  </Popover>
                </div>
                <div className="flex items-center justify-end px-1">
                  <span className="text-start text-sm text-gray-700 dark:text-gray-300">
                    <TimeAgo time={lastSeen} />
                  </span>
                </div>
                <div className="flex items-center justify-end px-1">
                  <span className="text-start text-sm text-gray-700 dark:text-gray-300">
                    <TimeAgo time={firstSeen} />
                  </span>
                </div>
              </div>
            </Card>
            /* {rowEl.getVisibleCells().map((cellEl) => (
              <td key={cellEl.id}>{flexRender(cellEl.column.columnDef.cell, cellEl.getContext())}</td>
            ))} */
          );
        })}
      </div>
    </div>
  );
}
